import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  X,
  Building,
  MapPin,
  Info,
  AlertTriangle,
  Phone,
  Navigation,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Route,
  Map,
  Check,
  UserPlus,
  ArrowLeft,
  Calendar,
  PaintBucket,
  ListCheck,
  SortAsc, // Or a suitable icon for sorting
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'; // Assuming this path is correct
import { InspectionCard } from '../components/inspection-card';
import { useAuth } from 'react-oidc-context';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { useTranslationWithLanguage } from '../context/language-context';
import { createPortal } from 'react-dom';
import SuggestedInspectionsPage from './SuggestedInspections';
import RouteOptimizationPage from './RouteOptimization';
import { Stack } from '@phosphor-icons/react';
import { set } from 'date-fns';
import { showToast } from '@/utils/helpers';
import { Header } from '@/components/header';
import { shadow } from 'pdfjs-dist';

// Define types for establishments
interface Establishment {
  id: number;
  summary: string;
  description: string;
  emirate_id: number;
  emirate: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  establishment_id: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
  };
  assigned_to: string;
  assigned_by?: string; // Who assigned this task (self or other)
  assigned_to_user: {
    id: string;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  inspection_type_id: number;
  inspection_type: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  risk_score: number;
  risk_grade: string;
  risk_category?: string;
  hours: string;
  location: {
    lat: number;
    lng: number;
  };
  status: string;
  task_status_id?: number;
  distance?: number; // Distance from previous point in km
  groupCount?: number; // Number of inspections grouped together
  arrivalTime?: Date; // Estimated arrival time
  groupedItems?: Establishment[]; // For grouped establishments
}

// Base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// Google Maps configuration
const mapContainerStyle = {
  width: '100%',
  height: '100vh',
};

const defaultCenter = {
  lat: 25.197197,
  lng: 55.274376,
};

const inspectorIcon = '<svg width="64px" height="64px" viewBox="-14.4 -14.4 76.80 76.80" xmlns="http://www.w3.org/2000/svg" fill="#ffffff" stroke="#ffffff" stroke-width="0.00048000000000000007"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="1.44"></g><g id="SVGRepo_iconCarrier"> <title>user-secure</title> <g id="Layer_2" data-name="Layer 2"> <g id="invisible_box" data-name="invisible box"> <rect width="48" height="48" fill="none"></rect> </g> <g id="icons_Q2" data-name="icons Q2"> <path d="M24,2S6,7.1,6,8V26.2c0,9.2,13.3,17.3,17,19.5a1.8,1.8,0,0,0,2,0c3.8-2.1,17-10.3,17-19.5V8C42,7.1,24,2,24,2Zm0,39.6a54,54,0,0,1-8.4-6.1A25.3,25.3,0,0,1,24,34a24.8,24.8,0,0,1,8.4,1.5A44.7,44.7,0,0,1,24,41.6ZM38,26.2c0,1.6-.8,3.7-2.6,6.1A30.9,30.9,0,0,0,24,30a30,30,0,0,0-11.3,2.3c-1.9-2.4-2.7-4.5-2.7-6.1V10.5c2.9-1.1,8.7-2.8,14-4.3,5.3,1.5,11.1,3.3,14,4.3Z"></path> <path d="M24,14a4,4,0,1,1-4,4,4,4,0,0,1,4-4m0-4a8,8,0,1,0,8,8,8,8,0,0,0-8-8Z"></path> </g> </g> </g></svg>';
const libraries: ('places' | 'geometry' | 'marker' | 'drawing')[] = ['places', 'geometry', 'marker', 'drawing'];

// Helper function to calculate risk grade
const calculateRiskGrade = (item: any): string => {
  if (item.params?.risk_category) return item.params?.risk_category;
  return 'HIGH';
};

// Helper function to map API status to component status
const mapStatus = (statusDisplay: string, taskStatusId?: number): string => {
  if (taskStatusId === 6) {
    return 'assigned';
  }

  switch (statusDisplay?.toUpperCase()) {
    case 'ASSIGNED INSPECTOR':
    case 'ASSIGNED':
    case 'ASSIGNED TO INSPECTOR':
    case 'INSPECTOR ASSIGNED':
      return 'assigned';
    case 'INPROGRESS':
    case 'IN PROGRESS':
    case 'ACTIVE':
      return 'inProgress';
    case 'PAUSED':
      return 'paused';
    case 'COMPLETED':
    case 'DONE':
    case 'FINISHED':
      return 'completed';
    case 'POOL':
    case 'UNASSIGNED':
      return 'pool';
    default:
      if (taskStatusId === 6) {
        return 'assigned';
      }
      return 'assigned';
  }
};

// Custom AdvancedMarker component
const AdvancedMarker: React.FC<{
  position: google.maps.LatLngLiteral;
  icon: { url: string; scaledSize: google.maps.Size };
  item: any;
  onClick: () => void;
  isSelected: boolean;
  language: string;
  getRiskColorHex: (grade: string) => string;
  map: google.maps.Map | null;
}> = ({ position, icon, item, onClick, isSelected, language, getRiskColorHex, map }) => {
  const markerRef = useRef<any>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!window.google || !map) {
      console.error('Google Maps API not loaded or map not available');
      return;
    }

    try {
      // Check if Advanced Markers are available
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        // Create a div element for the marker content
        const element = document.createElement('div');
        element.innerHTML = `
          <div style="
            background-color: white;
            border-radius: 20px;
            box-shadow: 0 2px 7px 1px rgba(0, 0, 0, 0.3);
            padding: 3px;
            display: flex;
            align-items: center;
            position: relative;
          ">
            <div style="
              width: 30px;
              height: 30px;
              border-radius: 50%;
              background-color: ${getRiskColorHex(item.risk_grade)};

              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 12px;
            ">!</div>

            <div style="
              position: absolute;
              bottom: -6px;
              left: 50%;
              transform: translateX(-50%);
              width: 0;
              height: 0;
              border-left: 8px solid transparent;
              border-right: 8px solid transparent;
              border-top: 8px solid white;
            "></div>
          </div>
        `; //<div style="font-size: 20px; font-weight: bold; padding: 4px;">1.1</div>

        // Create AdvancedMarkerElement
        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position,
          map,
          content: element,
        });

        // Create InfoWindow
        infoWindowRef.current = new google.maps.InfoWindow({
          content: `
            <div>
              <h3>${language === 'en' ? item.establishment.name_en : item.establishment.name_ar}</h3>
              <p>${language === 'en' ? item.inspection_type.name_en : item.inspection_type.name_ar}</p>
              <p style="color: ${getRiskColorHex(item.risk_grade)}; font-weight: bold;">
                Risk Grade: ${item.risk_grade}
              </p>
            </div>
          `,
          position,
        });

        // Add click listener using 'gmp-click'
        const clickListener = markerRef.current.addListener('gmp-click', () => {
          onClick();
          if (isSelected) {
            infoWindowRef.current?.open({ anchor: markerRef.current, map });
          } else {
            infoWindowRef.current?.close();
          }
        });

        // Cleanup
        return () => {
          try {
            if (clickListener && clickListener.remove) {
              clickListener.remove();
            }
            if (markerRef.current) {
              markerRef.current.map = null;
            }
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }
          } catch (error) {
            console.error('Error cleaning up marker:', error);
          }
        };
      } else {
        // Fallback to standard marker if Advanced Markers are not available
        console.warn("Advanced Markers not available, falling back to standard marker");

        markerRef.current = new google.maps.Marker({
          position,
          map,
          icon,
        });

        infoWindowRef.current = new google.maps.InfoWindow({
          content: `
            <div>
              <h3>${language === 'en' ? item.establishment.name_en : item.establishment.name_ar}</h3>
              <p>${language === 'en' ? item.inspection_type.name_en : item.inspection_type.name_ar}</p>
              <p style="color: ${getRiskColorHex(item.risk_grade)}; font-weight: bold;">
                Risk Grade: ${item.risk_grade}
              </p>
            </div>
          `,
        });

        markerRef.current.addListener('click', () => {
          onClick();
          if (isSelected) {
            infoWindowRef.current?.open(map, markerRef.current);
          } else {
            infoWindowRef.current?.close();
          }
        });

        // Cleanup
        return () => {
          if (markerRef.current) {
            markerRef.current.setMap(null);
          }
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
        };
      }
    } catch (error) {
      console.error('Error creating marker:', error);
      return () => {};
    }
  }, [position, icon, item, onClick, isSelected, language, getRiskColorHex, map]);

  return null;
};

export default function MapPage() {
  // This is a modified version of the Map page that incorporates functionality from Plan.tsx
  // while using Google Maps instead of Mapbox
  const auth = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslationWithLanguage();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState<string | null>(null);
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  type BottomSheetContent = 'inspections' | 'suggestions' | 'routes';
  const [bottomSheetContent, setBottomSheetContent] = useState<BottomSheetContent>('inspections');
  const [bottomSheetHeight, setBottomSheetHeight] = useState('65%');
  const [activeFilter, setActiveFilter] = useState('adhoc');
  const [selectedInfoWindow, setSelectedInfoWindow] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<google.maps.LatLngLiteral | null>(null);
  const [isAdhoc, setIsAdHoc] = useState(false);

  // Route optimization states
  const [optimizedRoute, setOptimizedRoute] = useState<Establishment[]>([]);
  const [routeInfo, setRouteInfo] = useState<{
    totalDistance: number;
    totalTime: number;
  } | null>(null);
  const [routePolyline, setRoutePolyline] = useState<google.maps.Polyline | null>(null);
  const [routeMarkers, setRouteMarkers] = useState<google.maps.Marker[]>([]);
  const [navigationActive, setNavigationActive] = useState(false);
  const [navigationSteps, setNavigationSteps] = useState<google.maps.DirectionsStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(null);

  // Bulk assignment states
  const [selectedInspections, setSelectedInspections] = useState<string[]>([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [bulkAssignResults, setBulkAssignResults] = useState({
    success: 0,
    failed: 0,
    total: 0,
  });
  const [showBulkAssignToast, setShowBulkAssignToast] = useState(false);
  const [bulkAssignToastType, setBulkAssignToastType] = useState<'success' | 'error'>('success');
  const [bulkAssignToastMessage, setBulkAssignToastMessage] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(5);
  const [totalSelectedPoints, setTotalSelectedPoints] = useState(0);
  const [establishmentHashMap, setEstablishmentHashMap] = useState<any>({});

  // Sorting state
  type SortOptionValue = 'risk_grade' | 'name' | 'distance';

  interface SortOption {
    value: SortOptionValue;
    labelKey: string; // Translation key
    // icon?: React.ReactElement; // Optional: if we want icons per sort option
  }

  const [activeSortOption, setActiveSortOption] = useState<SortOptionValue>('risk_grade'); // Default sort

  const sortOptions: SortOption[] = [
    { value: 'risk_grade', labelKey: 'sortBy.riskGrade' },
    { value: 'name', labelKey: 'sortBy.establishmentName' },
    { value: 'distance', labelKey: 'sortBy.distance' },
  ];

  // Note: The following translation keys need to be added to the translation files:
  // - filters.labourAccommodation
  // - filters.complaints
  // - filters.orders

  // State to store counts for each filter type
  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({
    pool: 0,
    adhoc: 0,
    assigned: 0,
    inProgress: 0,
    paused: 0,
    completed: 0,
    // New filter types
    labourAccommodation: 0,
    complaints: 0,
    orders: 0
  });

  const filterButtons = [
    {
      id: 'adHoc',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'adhoc',
      contentType: null,
      count: filterCounts.adhoc
    },
    {
      id: 'myTasks',
      icon: <MapPin className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'assigned',
      contentType: null,
      count: filterCounts.assigned
    },
    {
      id: 'active',
      icon: <Route className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'inProgress',
      contentType: null,
      count: filterCounts.inProgress
    },
    {
      id: 'pool',
      icon: <Stack className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'pool',
      contentType: null,
      count: filterCounts.pool
    },
    {
      id: 'labourAccommodation',
      icon: <Building className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'labourAccommodation',
      contentType: null,
      count: filterCounts.labourAccommodation
    },
    {
      id: 'complaints',
      icon: <Phone className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'complaints',
      contentType: null,
      count: filterCounts.complaints
    },
    {
      id: 'orders',
      icon: <ListCheck className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'orders',
      contentType: null,
      count: filterCounts.orders
    },
    {
      id: 'paused',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'paused',
      contentType: null,
      count: filterCounts.paused
    },
    {
      id: 'completed',
      icon: <Info className="w-3.5 h-3.5" />,
      action: 'setFilter',
      filterValue: 'completed',
      contentType: null,
      count: filterCounts.completed
    }
  ];

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
    mapIds: [import.meta.env.VITE_GOOGLE_MAPS_MAP_ID],
  });

  // State to hold all establishments
  const [establishments, setEstablishments] = useState<Establishment[]>([]);

  // Hide webkit scrollbars
  useEffect(() => {
    const data = localStorage.getItem('activeFilter');
    if (data && data.length > 0) {
      setActiveFilter(data);
    }
    const style = document.createElement('style');
    style.textContent = `
      .hide-webkit-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Dispatch custom event when bottom sheet state changes
  useEffect(() => {
    const event = new CustomEvent('bottomSheetStateChange', {
      detail: { isExpanded: isBottomSheetExpanded },
    });
    window.dispatchEvent(event);
  }, [isBottomSheetExpanded]);

  // Fetch establishments data
  const fetchEstablishmentsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      let url = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100`;

      let conditionParam = {};
      if (activeFilter === 'pool') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '2' }] };
      } else if (activeFilter === 'adhoc') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, {"field":"assigned_by","operator":"ne","value":"self"}] };
      } else if (activeFilter === 'assigned') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, {"field":"assigned_by","operator":"eq","value":"self"}] };
      } else if (activeFilter === 'inProgress') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '14' }] };
      } else if (activeFilter === 'paused') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '22' }] };
      } else if (activeFilter === 'completed') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '15' }] };
        url = `${BASE_URL}/jsonapi/transform/completed_tasks?sortBy=created_at&sortOrder=desc&page=1&pageSize=100`;
      } else if (activeFilter === 'labourAccommodation') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'labour_accommodation' }] };
      } else if (activeFilter === 'complaints') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'complaint' }] };
      } else if (activeFilter === 'orders') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'order' }] };
      }

      let globalFilterParam = {};
      if (searchQuery) {
        globalFilterParam = {
          searchTerm: searchQuery,
          searchFields: ['establishment_name_en', 'id', 'establishment_name_ar', 'establishment_id'],
        };
      }


      if (Object.keys(conditionParam).length > 0) {
        url += `&condition=${encodeURIComponent(JSON.stringify(conditionParam))}`;
      }
      if (Object.keys(globalFilterParam).length > 0) {
        url += `&globalFilter=${encodeURIComponent(JSON.stringify(globalFilterParam))}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (activeFilter === 'adhoc' && data.data.length > 0) {
        setIsAdHoc(true);
        if (!isBottomSheetExpanded) {  setIsBottomSheetExpanded(true);  }
      }
      const eHM: any = {};
      data.data.forEach((obj: any, index: number) => {
        eHM[obj.id] = obj;
      })
      setEstablishmentHashMap(eHM);

      const mappedEstablishments = data.data.map((item: any) => ({
        id: item.task_id,
        summary: `Routine inspection with risk: ${item.params?.risk_category}`,
        description: item.params?.message || 'No description available',
        emirate_id: item.emirate_id,
        emirate: {
          id: item.emirate_id,
          name_en: item.emirate_display || '',
          name_ar: item.emirate_display || '',
        },
        establishment_id: item.establishment_id,
        establishment: {
          id: item.establishment_id,
          name_en: item.establishment?.name_en || '',
          name_ar: item.establishment?.name_ar || '',
        },
        assigned_to: item.assigned_to || '',
        assigned_by: item.assigned_by || '',
        assigned_to_user: {
          id: item.assigned_to || '',
          username: item.assigned_to || 'Unknown',
          email: '',
          first_name: '',
          last_name: '',
        },
        inspection_type_id: item.params?.inspection_type_id ? parseInt(item.params.inspection_type_id) : 0,
        inspection_type: {
          id: item.params?.inspection_type_id ? parseInt(item.params.inspection_type_id) : 0,
          name_en: item.params?.inspection_type_display || '',
          name_ar: item.params?.inspection_type_display || '',
        },
        risk_score: item.risk_score || 0,
        risk_grade: calculateRiskGrade(item),
        risk_category: item.params?.risk_category || '',
        status: mapStatus(item.task_status_display || '', item.task_status_id),
        task_status_id: item.task_status_id,
        hours: '',
        location: {
          lat: item.establishment?.latitude ? parseFloat(item.establishment.latitude) : 0,
          lng: item.establishment?.longitude ? parseFloat(item.establishment.longitude) : 0,
        },
      }));

      setEstablishments(mappedEstablishments);

      // Calculate counts for each filter type
      const fetchAllCounts = async () => {
        const counts: Record<string, number> = {
          pool: 0,
          adhoc: 0,
          assigned: 0,
          inProgress: 0,
          paused: 0,
          completed: 0,
          labourAccommodation: 0,
          complaints: 0,
          orders: 0
        };

        // Pool count (task_status_id = 2)
        const poolResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
          rules: [{ operator: 'eq', field: 'task_status_id', value: '2' }]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (poolResponse.ok) {
          const poolData = await poolResponse.json();
          counts.pool = poolData?.total || 0;
        }

        // AdHoc count (task_status_id = 6 and assigned_by != self)
        const adhocResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
          rules: [
            { operator: 'eq', field: 'task_status_id', value: '6' },
            { field: "assigned_by", operator: "ne", value: "self" }
          ]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (adhocResponse.ok) {
          const adhocData = await adhocResponse.json();
          counts.adhoc = adhocData?.total || 0;
        }

        // Assigned count (task_status_id = 6 and assigned_by = self)
        const assignedResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
          rules: [
            { operator: 'eq', field: 'task_status_id', value: '6' },
            { field: "assigned_by", operator: "eq", value: "self" }
          ]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          counts.assigned = assignedData?.total || 0;
        }

        // In Progress count (task_status_id = 14)
        const inProgressResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
          rules: [{ operator: 'eq', field: 'task_status_id', value: '14' }]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (inProgressResponse.ok) {
          const inProgressData = await inProgressResponse.json();
          counts.inProgress = inProgressData?.total || 0;
        }

        // Paused count (task_status_id = 22)
        const pausedResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
          rules: [{ operator: 'eq', field: 'task_status_id', value: '22' }]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (pausedResponse.ok) {
          const pausedData = await pausedResponse.json();
          counts.paused = pausedData?.total || 0;
        }

        // Completed count (task_status_id = 15)
        const completedResponse = await fetch(`${BASE_URL}/jsonapi/transform/completed_tasks?condition=${encodeURIComponent(JSON.stringify({
          rules: [{ operator: 'eq', field: 'task_status_id', value: '15' }]
        }))}&page=1&pageSize=1`, {
          headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        });
        if (completedResponse.ok) {
          const completedData = await completedResponse.json();
          counts.completed = completedData?.total || 0;
        }

        // Labour Accommodation count
        // const labourAccommodationResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
        //   rules: [{ operator: 'eq', field: 'task_type', value: 'labour_accommodation' }]
        // }))}&page=1&pageSize=1`, {
        //   headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        // });
        // if (labourAccommodationResponse.ok) {
        //   const labourAccommodationData = await labourAccommodationResponse.json();
        //   counts.labourAccommodation = labourAccommodationData?.total || 0;
        // }

        // Complaints count
        // const complaintsResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
        //   rules: [{ operator: 'eq', field: 'task_type', value: 'complaint' }]
        // }))}&page=1&pageSize=1`, {
        //   headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        // });
        // if (complaintsResponse.ok) {
        //   const complaintsData = await complaintsResponse.json();
        //   counts.complaints = complaintsData?.total || 0;
        // }

        // Orders count
        // const ordersResponse = await fetch(`${BASE_URL}/jsonapi/transform/task_list?condition=${encodeURIComponent(JSON.stringify({
        //   rules: [{ operator: 'eq', field: 'task_type', value: 'order' }]
        // }))}&page=1&pageSize=1`, {
        //   headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
        // });
        // if (ordersResponse.ok) {
        //   const ordersData = await ordersResponse.json();
        //   counts.orders = ordersData?.total || 0;
        // }

        setFilterCounts(counts);
      };

      // Fetch counts for all filters
      try {
        await fetchAllCounts();
      } catch (error) {
        console.error('Error fetching filter counts:', error);
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to fetch establishments:', error);
      setError('Failed to fetch establishments. Please try again.');
    }
  }, [activeFilter, debouncedSearchQuery, auth?.user?.access_token]);

  useEffect(() => {
    fetchEstablishmentsData().catch((error) => {
      console.error('Failed to fetch establishments:', error);
    });
  }, [activeFilter, debouncedSearchQuery, fetchEstablishmentsData]);

  // Debounce search query
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 1500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Load assigned inspections from localStorage
  useEffect(() => {
    try {
      const assignedInspectionsJson = localStorage.getItem('assignedInspections');
      if (assignedInspectionsJson && establishments.length > 0) {
        const assignedInspections = JSON.parse(assignedInspectionsJson);
        if (Array.isArray(assignedInspections) && assignedInspections.length > 0) {
          const existingIds = new Set(establishments.map((e) => e.id));
          const newAssignedInspections = assignedInspections.filter(
            (inspection: Establishment) => !existingIds.has(inspection.id)
          );
          const formattedAssignedInspections = newAssignedInspections.map(
            (inspection: Establishment) => ({
              ...inspection,
              status: 'assigned',
            })
          );
          if (formattedAssignedInspections.length > 0) {
            setEstablishments((prev) => [...prev, ...formattedAssignedInspections]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading assigned inspections from localStorage:', error);
    }
  }, [establishments]);

  // Location tracking reference
  const watchPositionRef = useRef<number | null>(null);

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoading(false);

    // Start tracking user location immediately
    if (navigator.geolocation) {
      // First get initial position quickly
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const currentPos = { lat: latitude, lng: longitude };

          setCurrentLocation(currentPos);

          // Center map on current location and zoom in
          map.panTo(currentPos);
          map.setZoom(18);
        },
        (error) => {
          console.error('Error getting initial location:', error);
          // If we can't get location, still show establishments
          if (establishments.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            establishments.forEach((item) => {
              bounds.extend({ lat: item.location.lat, lng: item.location.lng });
            });
            map.fitBounds(bounds, 100);
          }
        }
      );

      // Then start continuous tracking
      watchPositionRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const currentPos = { lat: latitude, lng: longitude };
          setCurrentLocation(currentPos);

          // Center map on current location while maintaining zoom level
          const currentZoom = map.getZoom();
          map.panTo(currentPos);
          if (currentZoom && currentZoom < 15) {
            map.setZoom(18); // Only zoom in if we're zoomed out too far
          }
        },
        (error) => {
          console.error('Error tracking location:', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10000, // Accept positions that are up to 10 seconds old
          timeout: 5000 // Wait up to 5 seconds for a position
        }
      );
    } else {
      // Fallback if geolocation is not available
      if (establishments.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        establishments.forEach((item) => {
          bounds.extend({ lat: item.location.lat, lng: item.location.lng });
        });
        map.fitBounds(bounds, 100);
      }
    }
  }, [establishments]);

  // Clean up location tracking when component unmounts
  useEffect(() => {
    return () => {
      if (watchPositionRef.current !== null) {
        navigator.geolocation.clearWatch(watchPositionRef.current);
      }
    };
  }, []);

  // Create custom markers
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const newMarkers = establishments.map((item) => {
      const riskColor = getRiskColorHex(item.risk_grade);
      const icon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="60" height="40" viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(0, 0)">
              <rect x="2" y="2" width="56" height="24" rx="12" fill="white" filter="url(#shadow)"/>
              <circle cx="14" cy="14" r="10" fill="${riskColor}"/>
              <text x="14" y="18" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">!</text>
              <text x="36" y="18" font-family="Arial" font-size="12" font-weight="bold" fill="#333333" text-anchor="middle">1.1</text>
              <path d="M30 34L38 26H22L30 34Z" fill="white"/>
            </g>
            <defs>
              <filter id="shadow" x="0" y="0" width="60" height="28" filterUnits="userSpaceOnUse">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
                <feOffset dy="2"/>
                <feGaussianBlur stdDeviation="1"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
              </filter>
            </defs>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(60, 40),
      };

      return {
        id: String(item.id),
        position: { lat: item.location.lat, lng: item.location.lng },
        icon,
        item,
      };
    });

    setMarkers(newMarkers);
  }, [isLoaded, establishments]);

  // Create current location marker with blinking/sonar effect
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !currentLocation) return;
    localStorage.setItem('cL', JSON.stringify(currentLocation));
    console.log("Creating current location marker at:", currentLocation);

    // Create a standard Google Maps marker for the current location
    const locationMarker = new google.maps.Marker({
      position: currentLocation,
      map: mapRef.current,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#4285F4',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
      },
      zIndex: 9999,
      title: 'Your Location'
    });

    // Add a pulsing effect with a circle overlay
    const pulsingCircle = new google.maps.Circle({
      strokeColor: '#4285F4',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4285F4',
      fillOpacity: 0.35,
      map: mapRef.current,
      center: currentLocation,
      radius: 50,  // in meters
      zIndex: 9998
    });

    // Animate the circle for sonar effect
    let direction = 1;
    let radius = 50;
    const animationInterval = setInterval(() => {
      radius += direction * 2;
      if (radius > 70) direction = -1;
      if (radius < 30) direction = 1;
      pulsingCircle.setRadius(radius);
    }, 100);

    // Clean up
    return () => {
      locationMarker.setMap(null);
      pulsingCircle.setMap(null);
      clearInterval(animationInterval);
    };
  }, [isLoaded, currentLocation, activeFilter]);

  // Toggle bottom sheet
  const toggleBottomSheet = () => {
    setIsBottomSheetExpanded(!isBottomSheetExpanded);
  };

  // Show suggestions
  const showSuggestions = () => {
    setBottomSheetContent('suggestions');
    setBottomSheetHeight('65%');
    setIsBottomSheetExpanded(true);
  };

  const showSchedule = () => {
    navigate('/schedule');
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Radius of the earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  };

  // Show route optimization starting from current location
  const showRouteOptimization = () => {
    setBottomSheetContent('routes');
    setBottomSheetHeight('65%');
    setIsBottomSheetExpanded(true);

    // Always use current location as starting point if available
    if (currentLocation) {
      calculateOptimalRoute(currentLocation);
    } else {
      // If current location is not available yet, try to get it
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentLocation(userLoc);
          calculateOptimalRoute(userLoc);
        },
        (error) => {
          console.error('Error getting location for route optimization:', error);
          // Only fall back to default center if absolutely necessary
          calculateOptimalRoute();
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }
  };

  // Recalculate route based on current location
  const recalculateRoute = () => {
    // Clear existing route visualization
    if (routePolyline) {
      routePolyline.setMap(null);
    }

    routeMarkers.forEach(marker => {
      marker.setMap(null);
    });

    setRouteMarkers([]);

    // Get fresh location and recalculate
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLoc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentLocation(userLoc);
        calculateOptimalRoute(userLoc);
      },
      (error) => {
        console.error('Error getting location for recalculation:', error);
        // Use last known location if available, otherwise recalculate with existing data
        if (currentLocation) {
          calculateOptimalRoute(currentLocation);
        } else {
          calculateOptimalRoute();
        }
      }
    );
  };

  // Calculate optimal route
  const calculateOptimalRoute = async (startLoc?: google.maps.LatLngLiteral) => {
    // Clear any existing route visualization
    if (routePolyline) {
      routePolyline.setMap(null);
    }

    routeMarkers.forEach(marker => {
      marker.setMap(null);
    });

    setRouteMarkers([]);

    // Filter assigned establishments
    const assignedTasks = establishments.filter((item) => item.status === 'assigned');

    if (assignedTasks.length === 0) {
      setOptimizedRoute([]);
      setRouteInfo(null);
      return;
    }

    // Prioritize the provided location, then current location from state, then try to get a fresh one
    let startLocation: google.maps.LatLngLiteral = startLoc || defaultCenter;

    // If no location was provided but we have a current location in state, use that
    if (!startLoc && currentLocation) {
      console.log("Using current location from state for route optimization:", currentLocation);
      startLocation = currentLocation;
    }
    // If we still don't have a location from the parameters, try to get a fresh one
    else if (!startLoc) {
      try {
        console.log("Getting fresh location for route optimization");
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          });
        });

        const freshLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        // Update the current location state with this fresh location
        setCurrentLocation(freshLocation);
        startLocation = freshLocation;
        console.log("Got fresh location for route optimization:", startLocation);
      } catch (error) {
        console.error('Error getting location for route optimization:', error);
        // Only use default center as absolute last resort if we don't have current location
        if (!currentLocation) {
          console.warn("Falling back to default center for route optimization");
          // startLocation is already set to defaultCenter above
        } else {
          startLocation = currentLocation;
        }
      }
    }

    // Group establishments by establishment_id
    const establishmentGroups: Record<string, Establishment[]> = {};

    assignedTasks.forEach(task => {
      if (!establishmentGroups[task.establishment_id]) {
        establishmentGroups[task.establishment_id] = [];
      }
      establishmentGroups[task.establishment_id].push(task);
    });

    // Implement nearest-neighbor algorithm to find the optimal route
    let currentPoint: google.maps.LatLngLiteral = { ...startLocation };
    const visitedEstablishmentIds = new Set<string>();
    const optimizedEstablishments: Establishment[] = [];
    const routePoints: google.maps.LatLngLiteral[] = [startLocation];

    // Get all establishment groups as an array
    const allEstablishmentGroups = Object.values(establishmentGroups);

    // Continue until all establishments are visited
    while (visitedEstablishmentIds.size < allEstablishmentGroups.length) {
      let nearestEstablishment: Establishment | null = null;
      let minDistance = Infinity;
      let nearestGroupIndex = -1;

      // Find the nearest unvisited establishment
      allEstablishmentGroups.forEach((group, index) => {
        const establishmentId = group[0].establishment_id;

        // Skip if already visited
        if (visitedEstablishmentIds.has(establishmentId)) {
          return;
        }

        // Calculate distance from current point to this establishment
        const establishment = group[0];
        const distance = calculateDistance(
          currentPoint.lat,
          currentPoint.lng,
          establishment.location.lat,
          establishment.location.lng
        );

        // Update nearest if this is closer
        if (distance < minDistance) {
          minDistance = distance;
          nearestEstablishment = establishment;
          nearestGroupIndex = index;
        }
      });

      // If we found a nearest establishment
      if (nearestEstablishment !== null && nearestGroupIndex !== -1) {
        const establishment = nearestEstablishment as Establishment;
        const establishmentId = establishment.establishment_id;
        const group = allEstablishmentGroups[nearestGroupIndex];

        // Mark as visited
        visitedEstablishmentIds.add(establishmentId);

        // Add distance to the establishment
        const establishmentWithDistance = {
          ...establishment,
          distance: minDistance,
          groupCount: group.length
        };

        // Add to optimized route
        optimizedEstablishments.push(establishmentWithDistance);

        // Update current point for next iteration
        currentPoint = {
          lat: establishment.location.lat,
          lng: establishment.location.lng
        };

        // Add to route points
        routePoints.push(currentPoint);
      }
    }

    // Calculate total distance and time
    let totalDistance = 0;
    for (let i = 1; i < routePoints.length; i++) {
      const distance = calculateDistance(
        routePoints[i-1].lat, routePoints[i-1].lng,
        routePoints[i].lat, routePoints[i].lng
      );
      totalDistance += distance;
    }

    // Estimate time (assuming average speed of 40 km/h with traffic)
    // Convert to minutes: distance (km) / speed (km/h) * 60 min/h
    const totalTimeMinutes = (totalDistance / 40) * 60;

    // Ensure minimum reasonable time (at least 2 minutes per km for very short distances)
    const minimumTimeMinutes = Math.max(totalTimeMinutes, totalDistance * 2);

    setOptimizedRoute(optimizedEstablishments);
    setRouteInfo({
      totalDistance,
      totalTime: totalTimeMinutes
    });

    // Draw the route on the map if map is available
    if (mapRef.current) {
      // Use Directions Service to get actual driving routes
      const directionsService = new google.maps.DirectionsService();
      const directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true, // We'll add our own markers
        polylineOptions: {
          strokeColor: '#ab761b',
          strokeOpacity: 0.8,
          strokeWeight: 4
        },
        map: mapRef.current
      });

      // Create waypoints from the optimized establishments
      const waypoints = optimizedEstablishments.map(establishment => ({
        location: new google.maps.LatLng(
          establishment.location.lat,
          establishment.location.lng
        ),
        stopover: true
      }));

      // If we have establishments, calculate the route
      if (optimizedEstablishments.length > 0) {
        directionsService.route({
          origin: startLocation,
          destination: optimizedEstablishments.length > 0
            ? new google.maps.LatLng(
                optimizedEstablishments[optimizedEstablishments.length - 1].location.lat,
                optimizedEstablishments[optimizedEstablishments.length - 1].location.lng
              )
            : startLocation,
          waypoints: waypoints.slice(0, -1), // Remove the last waypoint as it's the destination
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false, // We've already optimized the route
          drivingOptions: {
            departureTime: new Date(), // Use current time for traffic calculations
            trafficModel: google.maps.TrafficModel.BEST_GUESS
          }
        }, (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            directionsRenderer.setDirections(result);

            // Update distances and times based on the actual route
            if (result.routes.length > 0 && result.routes[0].legs.length > 0) {
              let totalDistance = 0;
              let totalDuration = 0;

              // Extract all navigation steps for turn-by-turn directions
              const allSteps: google.maps.DirectionsStep[] = [];
              result.routes[0].legs.forEach(leg => {
                leg.steps.forEach(step => {
                  allSteps.push(step);
                });
              });
              setNavigationSteps(allSteps);

              // Update each establishment with the actual distance, duration, and arrival time
              let cumulativeTime = 0;
              const now = new Date();

              console.log("Route legs:", result.routes[0].legs.map(leg => ({
                distance: leg.distance?.value ? leg.distance.value / 1000 : 0,
                duration: leg.duration?.value ? leg.duration.value / 60 : 0
              })));

              result.routes[0].legs.forEach((leg, index) => {
                if (index < optimizedEstablishments.length) {
                  // Safely extract distance value
                  const distanceKm = leg.distance?.value ? leg.distance.value / 1000 : 0; // Convert to km
                  optimizedEstablishments[index].distance = distanceKm;
                  totalDistance += distanceKm;

                  // Calculate a reasonable time based on distance
                  // Use a consistent speed model: 30 km/h in city, minimum 2 min/km
                  const calculatedMinutes = Math.max(distanceKm * 2, distanceKm * 60 / 30);

                  // Safely extract duration value
                  const googleDurationMinutes = leg.duration?.value ? leg.duration.value / 60 : 0;

                  // Use the greater of Google's estimate or our calculated minimum
                  const legDurationMinutes = Math.max(googleDurationMinutes, calculatedMinutes);

                  totalDuration += legDurationMinutes;
                  cumulativeTime += legDurationMinutes;

                  console.log(`Stop ${index+1}: ${distanceKm.toFixed(1)}km, ${legDurationMinutes.toFixed(1)} mins`);

                  // Calculate arrival time
                  const arrivalTime = new Date(now.getTime() + (cumulativeTime * 60 * 1000));
                  optimizedEstablishments[index].arrivalTime = arrivalTime;
                }
              });

              // Update route info with actual values
              // Ensure minimum reasonable time (at least 2 minutes per km)
              const minimumTimeMinutes = totalDistance * 2;
              const finalDuration = Math.max(totalDuration, minimumTimeMinutes);

              console.log(`Total: ${totalDistance.toFixed(1)}km, ${finalDuration.toFixed(1)} mins`);

              setRouteInfo({
                totalDistance,
                totalTime: finalDuration
              });

              // Update optimized route with new distances
              setOptimizedRoute([...optimizedEstablishments]);
            }

            setRoutePolyline(directionsRenderer as any);
          } else {
            console.error('Directions request failed:', status);

            // Fallback to simple polyline if directions service fails
            const newPolyline = new google.maps.Polyline({
              path: routePoints,
              geodesic: true,
              strokeColor: '#ab761b',
              strokeOpacity: 0.8,
              strokeWeight: 4,
              map: mapRef.current
            });

            setRoutePolyline(newPolyline);
          }
        });
      } else {
        // If no establishments, just create an empty polyline
        const newPolyline = new google.maps.Polyline({
          path: [],
          geodesic: true,
          strokeColor: '#ab761b',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map: mapRef.current
        });

        setRoutePolyline(newPolyline);
      }

      // Add numbered markers for each stop
      const newMarkers: google.maps.Marker[] = [];

      optimizedEstablishments.forEach((establishment, index) => {
        // Create a custom marker with the sequence number
        const markerLabel = {
          text: (index + 1).toString(),
          color: 'white',
          fontWeight: 'bold'
        };

        const marker = new google.maps.Marker({
          position: {
            lat: establishment.location.lat,
            lng: establishment.location.lng
          },
          map: mapRef.current,
          label: markerLabel,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: '#ab761b',
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 12
          },
          zIndex: 1000 + index
        });

        newMarkers.push(marker);
      });

      setRouteMarkers(newMarkers);

      // Fit bounds to show the entire route
      const bounds = new google.maps.LatLngBounds();
      routePoints.forEach(point => bounds.extend(point));
      mapRef.current.fitBounds(bounds, 100);
    }
  };

  // Reset bottom sheet
  const resetBottomSheet = () => {
    setBottomSheetContent('inspections');
    setBottomSheetHeight('65%');
  };

  // Handle inspection selection
  const handleInspectionSelect = (id: string) => {
    setSelectedInspections((prev) => {
      if (prev.includes(id)) {
        const newSelected = prev.filter((inspectionId) => inspectionId !== id);
        calculateTotalPoints(newSelected);
        return newSelected;
      } else {
        const newSelected = [...prev, id];
        calculateTotalPoints(newSelected);
        return newSelected;
      }
    });
  };

  // Calculate total points
  const calculateTotalPoints = (selectedIds: string[]) => {
    let total = 0;
    selectedIds.forEach((id) => {
      const inspection = establishments.find((insp) => insp.id === parseInt(id));
      if (inspection) {
        total += 1.1;
      }
    });
    setTotalSelectedPoints(total);
  };

  // Handle unassign task - this will be passed to the InspectionCard component
  const handleUnassignTask = async (taskId: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsLoading(true);

        const response = await fetch(
          `${BASE_URL}/sdp/rules/api/v2/task/actions/unassign`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.user?.access_token}`,
            },
            body: JSON.stringify({
              task_id: taskId,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to unassign task: ${response.status}`);
        }

        // Refresh the data
        await fetchEstablishmentsData();

        // Resolve the promise to indicate success
        resolve();
      } catch (error) {
        console.error('Error unassigning task:', error);
        // Reject the promise to indicate failure
        reject(error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  // Handle bulk assignment
  const handleBulkAssign = () => {
    if (selectedInspections.length > 0) {
      setShowBulkAssignDialog(true);
    }
  };

  // Confirm bulk assignment
  const confirmBulkAssignment = async () => {
    setIsBulkAssigning(true);
    setShowBulkAssignDialog(false);

    const total = selectedInspections.length;
    let success = 0;
    let failed = 0;

    setBulkAssignResults({ success: 0, failed: 0, total });

    const currentToken = auth?.user?.access_token;
    if (!currentToken) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(t('errors.notAuthenticated'));
      setShowBulkAssignToast(true);
      setIsBulkAssigning(false);
      return;
    }

    for (const inspectionId of selectedInspections) {
      try {
        const response = await fetch(
          `${BASE_URL}/sdp/rules/api/v2/task/actions/self_assign`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
            body: JSON.stringify({
              task_id: (inspectionId.indexOf('_') > -1 ? inspectionId.split('_') : inspectionId),
            }),
          }
        );

        const data = await response.json();
        if (response.ok && data.status === true) {
          success++;
        } else {
          failed++;
          console.error('Failed to assign task:', inspectionId, data);
        }
      } catch (error) {
        failed++;
        console.error('Error assigning task:', inspectionId, error);
      }
      setBulkAssignResults({ success, failed, total });
    }

    if (failed === 0) {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(t('common.bulkAssignSuccess', { count: success }));
    } else if (success === 0) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(t('common.bulkAssignFailed', { count: failed }));
    } else {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(t('common.bulkAssignPartial', { success, failed }));
    }

    setShowBulkAssignToast(true);
    setIsBulkAssigning(false);
    setSelectedInspections([]);
    setTotalSelectedPoints(0);

    if (success > 0) {
      window.dispatchEvent(
        new CustomEvent('inspection:bulk-assigned', { detail: { count: success } })
      );
      setShowCountdown(true);
      setCountdownSeconds(30);

      const countdownInterval = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowCountdown(false);
            setIsLoading(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const getRiskColorHex = (grade: string): string => {
    switch (grade) {
      case 'LOW': return '#00843d';
      case 'MEDIUM': return '#f97316';
      case 'HIGH': return '#c8102e';
      default: return '#b89256';
    }
  };

  // Use establishments directly from API response
  // The API already filters based on search query and active filter
  // const filteredEstablishments = establishments; // This will be replaced by sortedEstablishments

  const riskGradeOrder: Record<string, number> = {
    HIGH: 3,
    MEDIUM: 2,
    LOW: 1,
  };

  const sortedEstablishments = useMemo(() => {
    let sortableItems = [...establishments];

    switch (activeSortOption) {
      case 'risk_grade':
        sortableItems.sort((a, b) => {
          const gradeA = riskGradeOrder[a.risk_grade.toUpperCase()] || 0;
          const gradeB = riskGradeOrder[b.risk_grade.toUpperCase()] || 0;
          return gradeB - gradeA; // High to Low
        });
        break;
      case 'name':
        sortableItems.sort((a, b) =>
          a.establishment.name_en.localeCompare(b.establishment.name_en)
        );
        break;
      case 'distance':
        if (currentLocation) {
          sortableItems = sortableItems.map(item => ({
            ...item,
            // Calculate distance if not already present or to ensure it's from current location
            distance: calculateDistance(
              currentLocation.lat,
              currentLocation.lng,
              item.location.lat,
              item.location.lng
            ),
          })).sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        }
        // If no current location, distance sort might not be meaningful, could keep original order or sort by name as fallback
        break;
      default:
        // No sort or default sort (e.g., by ID or original fetch order)
        break;
    }
    return sortableItems;
  }, [establishments, activeSortOption, currentLocation]);


  // Group establishments by establishment_id
  const groupedEstablishments = useMemo(() => {
    const groups: Record<string, Establishment[]> = {};
    // Group establishments by establishment_id
    sortedEstablishments.forEach(item => { // Use sortedEstablishments here
      const key = item.establishment_id;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });
    // Convert groups to array of grouped establishments
    return Object.entries(groups).map(([establishmentId, items]) => {
      // Use the first item as the base
      const baseItem = items[0];
      const taskList: any = [];
      items.forEach(item => {
        taskList.push(establishmentHashMap[item.id]);
      })
      console.log("Grouped items:", taskList[0]);
      // Create a grouped item
      return {
        ...baseItem,
        id: items.map(i => i.id).join('_'), // Combine IDs for a unique identifier
        groupedItems: taskList, // Store all items in the group
        groupCount: items.length, // Store the count of items
        // For inspection type, show first type + count of additional types
        inspection_type: {
          ...baseItem.inspection_type,
          name_en: items.length > 1
            ? `${baseItem.inspection_type.name_en} + ${items.length - 1}`
            : baseItem.inspection_type.name_en,
          name_ar: items.length > 1
            ? `${baseItem.inspection_type.name_ar} + ${items.length - 1}`
            : baseItem.inspection_type.name_ar,
        }
      };
    });
  }, [sortedEstablishments]);

  // Bulk Assignment Dialog
  const BulkAssignmentDialog = () => {
    if (!showBulkAssignDialog) return null;
    return (
      <div className="fixed inset-0 bg-[#00000087] flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-2">{t('common.confirmBulkAssignment')}</h3>
          <p className="text-gray-600 mb-4">
            {t('common.bulkAssignmentConfirmMessage', { count: selectedInspections.length })}
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkAssignDialog(false)}
              className="border-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-[#b89256] text-white hover:opacity-90"
              onClick={confirmBulkAssignment}
            >
              <Check className="w-4 h-4 mr-2" />
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Bulk Assign Toast
  const BulkAssignToast = () => {
    if (!showBulkAssignToast) return null;
    return createPortal(
      <div className="fixed bottom-4 right-2 z-[9999]">
        <div
          className={`rounded-md shadow-md p-4 flex items-center ${
            bulkAssignToastType === 'success'
              ? 'bg-[#b89256] border-l-4 border-[#b89256]'
              : 'bg-red-50 border-l-4 border-red-500'
          }`}
        >
          <div
            className={`mr-3 ${bulkAssignToastType === 'success' ? 'text-white' : 'text-red-500'}`}
          >
            {bulkAssignToastType === 'success' ? (
              <Check className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1">
            <p
              className={`text-sm font-medium ${
                bulkAssignToastType === 'success' ? 'text-white' : 'text-red-800'
              }`}
            >
              {bulkAssignToastMessage}
            </p>
          </div>
          <button
            onClick={() => setShowBulkAssignToast(false)}
            className="ml-4 text-gray-400 hover:text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>,
      document.body
    );
  };

  if (loadError) {
    return (
      <div className="absolute inset-0 z-10">
        <div className="absolute inset-0 bg-gray-100">
          <iframe
            className="w-full h-full border-0"
            src={`https://embed.waze.com/iframe?zoom=12&lat=25.197197&lon=55.274376&ct=livemap`}
            allowFullScreen
            loading="lazy"
            title="Waze Map"
          ></iframe>
          <div className="absolute top-0 left-0 right-0 bg-white bg-opacity-90 p-2 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#b89256] flex items-center justify-center">
                <MapPin className="w-3 h-3 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-700">{t('map.usingWazeMap')}</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 py-0 text-xs bg-[var(--color-gold-500)] text-white hover:opacity-90 rounded-full"
                onClick={() => window.location.reload()}
              >
                {t('map.retry')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Header title={
                language === 'en'
                  ?
                    'Inspection Details'
                  :
                    'تفاصيل المنشأة'
              }
              isRTL={language === 'ar'}
              showBackButton={false}
              backTo="/plan"
            />
      <div className={`h-[95vh] relative overflow-hidden touch-none`}>
        {/* ${isBottomSheetExpanded ? 'h-[45vh]' : 'h-[95vh]'}  */}
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={defaultCenter}
            zoom={10}
            onLoad={onMapLoad}
            options={{
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: false,
              zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT
              },
              mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || '21f0b4813008025'
            }}
          >
            {markers.map((marker) => (
              <AdvancedMarker
                key={marker.id}
                position={marker.position}
                icon={marker.icon}
                item={marker.item}
                onClick={() => {
                  setSelectedInfoWindow(marker.id);
                  setSelectedEstablishment(marker.id);
                  if (!isBottomSheetExpanded) {
                    setIsBottomSheetExpanded(true);
                  }
                }}
                isSelected={selectedInfoWindow === marker.id}
                language={language}
                getRiskColorHex={getRiskColorHex}
                map={mapRef.current} // Pass the map prop
              />
            ))}
            {/* Current location marker is created in useEffect below */}
          </GoogleMap>
        ) : (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
            <div className="text-center p-4">
              <div className="w-12 h-12 border-4 border-amber-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">{t('map.loadingMap')}</h3>
              <p className="text-sm text-gray-500">{t('map.pleaseWait')}</p>
            </div>
          </div>
        )}

        {(mapLoading && establishments.length > 0 || isLoading) && !mapError && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
            <div className="text-center p-4">
              <div className="w-12 h-12 border-4 border-amber-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {isLoading ? t('common.loading') : t('map.loadingMap')}
              </h3>
              <p className="text-sm text-gray-500">{t('map.pleaseWait')}</p>
            </div>
          </div>
        )}

        {error && !mapError && !isLoading && (
          <div className="absolute top-20 left-0 right-0 mx-auto w-max bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-4 text-xs"
                onClick={() => fetchEstablishmentsData()}
              >
                {t('common.retry')}
              </Button>
            </div>
          </div>
        )}

        <div className="absolute top-4 left-0 right-0 mx-auto w-[90%] z-30">
          <div className="relative">
            <div className="bg-white rounded-lg shadow-lg flex items-center h-10 px-4 max-w-md mx-auto">
              <Search className="w-5 h-5 text-gray-500 mr-2" />
              <Input
                placeholder={t('plan.searchEstablishments')}
                className="border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(e.target.value);
                  // The actual API call will be triggered by the debounced value
                }}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              {/* <div className="ml-2">
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-md bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('plan.scheduleMeeting') || 'Schedule Meeting'}</span>
                </Button>
              </div> */}
            </div>
          </div>

          <div
            className="mt-4 flex gap-1 items-center overflow-x-auto py-2 px-0 w-full hide-webkit-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {filterButtons.map((button) => (
              <Button
                key={button.id}
                variant="ghost"
                size="sm"
                className={`rounded-lg flex flex-row h-fit text-nowrap items-center justify-center gap-1 px-3 py-1 ${
                  (button.contentType ? bottomSheetContent === button.contentType : activeFilter === button.filterValue)
                    ? 'bg-[#b89256] text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 shadow-sm'
                }`}
                onClick={() => {
                  if (activeFilter === button.filterValue) {
                    return;
                  } else if ((button.filterValue != 'adhoc' && button.filterValue != 'inProgress' && button.filterValue != 'paused' && button.filterValue != 'completed') && isAdhoc) {
                    showToast('error', t('filters.adhocFilterError'));
                    return;
                  } else if (button.action === 'showSuggestions') {
                    showSuggestions();
                    if (button.filterValue) setActiveFilter(button.filterValue);
                  } else if (button.action === 'showRouteOptimization') {
                    showRouteOptimization();
                    if (button.filterValue) setActiveFilter(button.filterValue);
                  } else if (button.action === 'setFilter' && button.filterValue) {
                    setBottomSheetContent('inspections'); setIsBottomSheetExpanded(true);
                    setActiveFilter(button.filterValue);
                  }
                  localStorage.setItem('activeFilter', button.filterValue);
                }}
              >
                <div className="flex items-center gap-1">
                  {button.icon}
                  <span className="text-xs font-medium">{t(`filters.${button.id}`)}</span>
                  <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full ${
                    (button.contentType ? bottomSheetContent === button.contentType : activeFilter === button.filterValue)
                      ? 'bg-white text-[#b89256]'
                      : 'bg-[#b89256] text-white'
                  }`}>
                    {button.count > 99 ? '99+' : button.count}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Map Control Buttons - Always visible */}
        <div className={`absolute right-2 z-30 flex flex-col gap-2 right-[1rem] ${
          isBottomSheetExpanded ? 'top-[20%]' : 'bottom-[7rem]'
        }`}>
          {/* Schedule */}
          {/* <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={showSchedule}
              title={t('plan.schedule') || 'Schedule'}
            >
              <Calendar className="w-5 h-5 text-[#b89256]" />
            </Button>
          </div> */}

          <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={() => {
                if (mapRef.current && establishments.length > 0) {
                  const bounds = new google.maps.LatLngBounds();
                  establishments.forEach((item) => {
                    if (item.location.lat && item.location.lng) {
                      bounds.extend({ lat: item.location.lat, lng: item.location.lng });
                    }
                  });
                  mapRef.current.fitBounds(bounds, 100);
                }
              }}
              title={t('map.fitToScreen') || 'Fit to Screen'}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b89256" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
            </Button>
          </div>

          {/* Fit to Screen Button */}
          <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={() => {
                if (mapRef.current && establishments.length > 0) {
                  const bounds = new google.maps.LatLngBounds();
                  establishments.forEach((item) => {
                    if (item.location.lat && item.location.lng) {
                      bounds.extend({ lat: item.location.lat, lng: item.location.lng });
                    }
                  });
                  mapRef.current.fitBounds(bounds, 100);
                }
              }}
              title={t('map.fitToScreen') || 'Fit to Screen'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4H9M4 4V9M4 4L9 9M20 4H15M20 4V9M20 4L15 9M4 20H9M4 20V15M4 20L9 15M20 20H15M20 20V15M20 20L15 15"
                  stroke="#b89256" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>

          {/* Current Location Button removed - now automatically tracking */}

          {/* Route Optimization Button - Only show when assigned filter is active */}
          {activeFilter === 'assigned' && (
            <div className="mb-2">
              <Button
                variant="ghost"
                size="icon"
                className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
                onClick={showRouteOptimization}
                title={t('plan.routeOptimization') || 'Route Optimization'}
              >
                <Route className="w-5 h-5 text-[#b89256]" />
              </Button>
            </div>
          )}

          {/* Suggested Inspections Button */}
          <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={() => {
                if (activeFilter && isAdhoc) {
                    showToast('error', t('filters.adhocFilterError'));
                    return;
                } else {
                  showSuggestions();
                }
              }}
              title={t('plan.suggestedInspections') || 'Suggested Inspections'}
            >
              <Sparkles className="w-5 h-5 text-[#b89256]" />
            </Button>
          </div>
        </div>

        <div
          className={`absolute bottom-0 left-0 right-0 bg-white shadow-2xl z-20 transition-all duration-300 ease-in-out rounded-xl overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent`}
          style={{
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            overflowY: isBottomSheetExpanded ? 'auto' : 'hidden',
            overflowX: 'hidden',
            height: isBottomSheetExpanded ? bottomSheetHeight : '100px',
            boxShadow: '0px 0px 10px 2px',
          }}
        >
          <div
            className="relative w-full flex justify-center items-center pt-3 pb-3 cursor-pointer"
            onClick={() => {
              if (isBottomSheetExpanded) {
                setIsBottomSheetExpanded(false);
                resetBottomSheet();
              } else {
                setIsBottomSheetExpanded(true);
              }
            }}
          >
            <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
          </div>

          {!isBottomSheetExpanded && (
            <div className="px-4 py-2">
              <h2 className="text-lg font-medium">
                {selectedEstablishment
                  ? establishments.find((e) => String(e.id) === selectedEstablishment)?.establishment.name_en
                  : t('plan.inspections')}
              </h2>
              <div className="flex mt-1">
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-6 h-6 rounded-full bg-[#b89256] flex items-center justify-center mr-1">
                    <MapPin className="w-3 h-3 text-white" />
                  </div>
                  <span>
                    {selectedEstablishment
                      ? establishments.find((e) => String(e.id) === selectedEstablishment)?.emirate.name_en
                      : t('plan.selectLocation')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {isBottomSheetExpanded && (
            <div className="px-4 pb-20">
              {bottomSheetContent === 'inspections' && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{t('plan.inspections')}</h2>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-lg text-xs px-3">
                          <SortAsc className="w-3 h-3 mr-1.5" />
                          <span>
                            {t(sortOptions.find(opt => opt.value === activeSortOption)?.labelKey || 'common.sortBy')}
                          </span>
                          <ChevronDown className="w-3 h-3 ml-1.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align={language === 'ar' ? 'start' : 'end'} className="bg-white">
                        {sortOptions.map((option) => (
                          <DropdownMenuItem
                            key={option.value}
                            onClick={() => setActiveSortOption(option.value)}
                            className={`text-xs ${activeSortOption === option.value ? 'bg-accent text-accent-foreground' : ''}`}
                          >
                            {/* Optional: Add icon here if defined in sortOptions */}
                            {t(option.labelKey)}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-[200px]">
                      <div className="w-12 h-12 border-4 border-[#4285F4] border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 font-medium">{t('common.loading')}</p>
                    </div>
                  ) : (
                    <div className="w-full overflow-y-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {groupedEstablishments.map((item) => (
                          <div
                            key={item.id}
                            className={`cursor-pointer ${
                              selectedEstablishment === String(item.id)
                                ? ''
                                : ''
                            }`}
                            onClick={() => {
                              setSelectedEstablishment(String(item.id));
                              setSelectedInfoWindow(String(item.id));
                              if (mapRef.current) {
                                mapRef.current.panTo({ lat: item.location.lat, lng: item.location.lng });
                                mapRef.current.setZoom(10);
                              }
                            }}
                          >
                            <InspectionCard
                              id={String(item.id)}
                              establishment_id={item.establishment_id}
                              establishment={item.establishment}
                              emirate={item.emirate}
                              inspection_type={item.inspection_type}
                              risk_grade={item.risk_grade}
                              risk_category={item.risk_category}
                              status={item.status}
                              language={language}
                              points={item.groupCount * 1.1}
                              groupCount={item.groupCount}
                              groupedItems={item.groupedItems}
                              // Pass assigned_by and onUnassign props
                              assigned_by={activeFilter === 'assigned' && item.groupedItems &&
                                item.groupedItems.some(groupItem => groupItem.assigned_by === 'self') ? 'self' : undefined}
                              onUnassign={activeFilter === 'assigned' ? async () => {
                                // For grouped items, unassign all tasks in the group that were assigned by self
                                if (item.groupedItems) {
                                  const promises = item.groupedItems
                                    .filter(groupItem => groupItem.assigned_by === 'self')
                                    .map(groupItem => handleUnassignTask(String(groupItem.id)));
                                  await Promise.all(promises);
                                }
                              } : undefined}
                              // Conditionally include onSelect and isSelected if supported
                              {...(handleInspectionSelect && {
                                onSelect: () => {
                                  // When selecting a grouped item, select all items in the group
                                  item.groupedItems.forEach(groupItem => {
                                    handleInspectionSelect(String(groupItem.id));
                                  });
                                },
                                isSelected: item.groupedItems.some(groupItem =>
                                  selectedInspections.includes(String(groupItem.id))
                                ),
                              })}
                            />
                          </div>
                        ))}
                        {groupedEstablishments.length === 0 && (
                          <div className="text-center py-8 col-span-full">
                            <Info className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600">{t('plan.noEstablishmentsFound')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {bottomSheetContent === 'suggestions' && (
                <div className="h-full">
                  <h2 className="text-xl font-bold mb-4">{t('plan.suggestedInspections')}</h2>
                  <div className="w-full  overflow-y-auto">
                    <SuggestedInspectionsPage />
                  </div>
                </div>
              )}

              {bottomSheetContent === 'routes' && (
                <div className="h-full">
                  <h2 className="text-xl font-bold mb-4">{t('plan.routeOptimization')}</h2>
                  <div className="w-full overflow-y-auto">
                    {/* Route stats */}
                    {routeInfo && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">Route Summary</h3>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs border border-[#b89256]"
                              onClick={recalculateRoute}
                            >
                              Recalculate Route
                            </Button>
                            {!navigationActive ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs bg-[#b89256] text-white hover:bg-[#b89256] border-none"
                                onClick={() => {
                                  setNavigationActive(true);
                                  setCurrentStepIndex(0);
                                  // Start voice guidance
                                  if (navigationSteps.length > 0 && 'speechSynthesis' in window) {
                                    const utterance = new SpeechSynthesisUtterance(
                                      "Starting navigation. " + navigationSteps[0].instructions.replace(/<[^>]*>/g, '')
                                    );
                                    utterance.voice = speechSynthesis.getVoices().find(voice => voice.name.includes('Female')) || null;
                                    utterance.rate = 0.9;
                                    speechSynthesis.speak(utterance);
                                  }
                                }}
                              >
                                <Navigation className="w-3 h-3 mr-1" />
                                Start My Trip
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs bg-red-600 text-white hover:bg-red-700 border-none"
                                onClick={() => {
                                  setNavigationActive(false);
                                  // Stop any ongoing speech
                                  if ('speechSynthesis' in window) {
                                    speechSynthesis.cancel();
                                  }
                                }}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Exit Navigation
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-500">Total Distance</p>
                            <p className="text-lg font-bold">{routeInfo.totalDistance.toFixed(1)} km</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-500">Estimated Time</p>
                            <p className="text-lg font-bold">
                              {routeInfo.totalTime >= 60
                                ? `${Math.floor(routeInfo.totalTime / 60)}h ${Math.round(routeInfo.totalTime % 60)}m`
                                : `${Math.round(routeInfo.totalTime)}m`}
                            </p>
                          </div>
                        </div>

                        {/* Navigation instructions */}
                        {navigationActive && navigationSteps.length > 0 && (
                          <div className="mt-4 border-t pt-3">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Turn-by-Turn Navigation</h4>
                            <div className="max-h-60 overflow-y-auto">
                              {navigationSteps.map((step, index) => (
                                <div
                                  key={index}
                                  className={`mb-2 border-l-2 ${index === currentStepIndex ? 'border-[#b89256] bg-[#b89256]' : 'border-gray-300'} pl-2 rounded-sm`}
                                >
                                  <div
                                    className="flex items-center cursor-pointer py-1"
                                    onClick={() => setExpandedStepIndex(expandedStepIndex === index ? null : index)}
                                  >
                                    <div className="w-6 h-6 rounded-full bg-[#b89256] text-white flex items-center justify-center text-xs mr-2">
                                      {index + 1}
                                    </div>
                                    <div
                                      className="text-xs flex-1"
                                      dangerouslySetInnerHTML={{ __html: step.instructions }}
                                    />
                                    <div className="text-xs text-gray-500 ml-2">
                                      {step.distance?.value ? (step.distance.value / 1000).toFixed(1) : '0'} km
                                    </div>
                                    {expandedStepIndex === index ? (
                                      <ChevronUp className="w-3 h-3 ml-1 text-gray-500" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3 ml-1 text-gray-500" />
                                    )}
                                  </div>

                                  {expandedStepIndex === index && (
                                    <div className="pl-8 pr-2 pb-2">
                                      <div className="text-xs text-gray-600 mb-1">
                                        <span className="font-medium">Duration:</span> {step.duration?.value ? Math.round(step.duration.value / 60) : 0} min
                                      </div>
                                      {step.transit && step.transit.line && (
                                        <div className="text-xs text-gray-600">
                                          <span className="font-medium">Transit:</span> {step.transit.line.name}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            <div className="flex justify-between mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                disabled={currentStepIndex === 0}
                                onClick={() => {
                                  if (currentStepIndex > 0) {
                                    const newIndex = currentStepIndex - 1;
                                    setCurrentStepIndex(newIndex);
                                    setExpandedStepIndex(newIndex);

                                    // Voice guidance
                                    if ('speechSynthesis' in window) {
                                      const utterance = new SpeechSynthesisUtterance(
                                        navigationSteps[newIndex].instructions.replace(/<[^>]*>/g, '')
                                      );
                                      utterance.voice = speechSynthesis.getVoices().find(voice => voice.name.includes('Female')) || null;
                                      utterance.rate = 0.9;
                                      speechSynthesis.speak(utterance);
                                    }
                                  }
                                }}
                              >
                                Previous Step
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                disabled={currentStepIndex === navigationSteps.length - 1}
                                onClick={() => {
                                  if (currentStepIndex < navigationSteps.length - 1) {
                                    const newIndex = currentStepIndex + 1;
                                    setCurrentStepIndex(newIndex);
                                    setExpandedStepIndex(newIndex);

                                    // Voice guidance
                                    if ('speechSynthesis' in window) {
                                      const utterance = new SpeechSynthesisUtterance(
                                        navigationSteps[newIndex].instructions.replace(/<[^>]*>/g, '')
                                      );
                                      utterance.voice = speechSynthesis.getVoices().find(voice => voice.name.includes('Female')) || null;
                                      utterance.rate = 0.9;
                                      speechSynthesis.speak(utterance);
                                    }
                                  }
                                }}
                              >
                                Next Step
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Optimized route cards */}
                    <div className="space-y-4">
                      {optimizedRoute.map((item, index) => (
                        <div key={item.id} className="flex flex-col gap-2">
                          {/* Route number and arrival time */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#b89256] text-white flex items-center justify-center font-bold shadow-md z-10">
                              {index + 1}
                            </div>
                            {item.arrivalTime && (
                              <div className="text-sm font-medium text-gray-700">
                                ETA: {item.arrivalTime.toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})}
                                { ' '}({item.distance ? `${item.distance.toFixed(1)}km` : ''})
                              </div>
                            )}
                          </div>
                          <div className="">
                            <InspectionCard
                              id={String(item.id)}
                              establishment_id={item.establishment_id}
                              establishment={item.establishment}
                              emirate={item.emirate}
                              inspection_type={item.inspection_type}
                              risk_grade={item.risk_grade}
                              risk_category={item.risk_category}
                              status={item.status}
                              language={language}
                              points={item.groupCount ? item.groupCount * 1.1 : 1.1}
                              groupCount={item.groupCount}
                              distance={item.distance}
                              // Pass assigned_by and onUnassign props
                              assigned_by={item.assigned_by}
                              onUnassign={item.assigned_by === 'self' ?
                                () => handleUnassignTask(String(item.id)) :
                                undefined}
                            />
                            {item.distance !== undefined && (
                              <div className="mt-1 ml-2 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <Navigation className="w-3 h-3 mr-1" />
                                  {item.distance.toFixed(1)} km {index < optimizedRoute.length - 1 ? 'to next' : 'from previous'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {optimizedRoute.length === 0 && (
                        <div className="text-center py-8">
                          <Route className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">No assigned establishments to optimize route</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <BulkAssignmentDialog />
        <BulkAssignToast />
      </div>
    </div>
  );
}
